import { useEffect, useRef } from 'react';
import { useChatContext } from '../hooks/useChat';
import Message from '../components/Message';
import Input from '../components/Input';
import TypingIndicator from '../components/TypingIndicator';

export default function ChatView() {
  const { messages, isLoading, isTyping, sendMessage, error, connectionState, activeToolCall } = useChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, activeToolCall]);

  return (
    <div className="flex flex-col h-full">
      {/* Connection status banner */}
      {connectionState === 'connecting' && (
        <div className="bg-yellow-50 border-b border-yellow-100 px-4 py-2 text-center">
          <p className="text-sm text-yellow-700 flex items-center justify-center gap-2">
            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
            Connecting...
          </p>
        </div>
      )}
      {(connectionState === 'disconnected' || connectionState === 'error') && (
        <div className="bg-red-50 border-b border-red-100 px-4 py-2 text-center">
          <p className="text-sm text-red-600 flex items-center justify-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full" />
            Connection lost. Reconnecting...
          </p>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-primary"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">
              Send a message to start the conversation
            </p>
          </div>
        )}

        {messages.map((message) => (
          <Message key={message.id} message={message} />
        ))}

        {/* Tool lookup indicator */}
        {activeToolCall && (
          <div className="flex items-center gap-2 px-4 py-2">
            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 rounded-2xl px-4 py-2.5 text-sm">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Looking up data{activeToolCall.serverName ? ` in ${activeToolCall.serverName}` : ''}...</span>
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {(isLoading || isTyping) && !activeToolCall && !messages.some((m) => m.isStreaming) && <TypingIndicator />}

        {error && (
          <div className="flex justify-center">
            <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <Input onSend={sendMessage} disabled={isLoading || connectionState !== 'connected'} />
    </div>
  );
}
